/**
 * The numbers a chat PRESET draws with (`CHAT_PRESETS`), in one place the
 * dashboard preview and the embed runtime both import - so the surface on a
 * customer's page renders the way the editor promised by construction, not
 * by keeping two copies in step. Pure constants: no zod, no React, nothing
 * the runtime bundle would have to carry.
 *
 * `facebook-messenger` is `main`'s preset carried across (2026-09-05): a
 * self-contained Messenger look - avatar launcher with a badge, a white
 * panel with a plain header, gradient visitor bubbles, an "Aa" composer -
 * that ignores the accent, alpha and radius; only placement and the
 * customer's css still apply.
 */
/**
 * The default bubble's box (2026-09-10): a FIXED panel, the way every chat
 * window people know behaves - the thread scrolls inside it, the composer
 * stays put, and the card is the same size before the first message and
 * after the fiftieth. Before this the panel was as tall as its messages
 * and grew as replies landed, which read as a tooltip, not a chat. The
 * numbers live here so the preview and the runtime draw the same box.
 */
export const CHAT_BUBBLE = {
  panelWidth: 340,
  panelHeight: 480,
  launcherSize: 44,
  /** Between the launcher's top and the panel's bottom. */
  panelGap: 10,
} as const

export const MESSENGER = {
  /** Solid purple - the launcher badge, the launcher's shadow tint. */
  accent: "#7B5BFF",
  /** The visitor's bubbles, the CTA pill, the launcher without an avatar. */
  gradient: "linear-gradient(135deg,#00B2FF 0%,#006AFF 38%,#8E5BFF 100%)",
  /** Facebook blue - the call buttons and the send glyph. */
  blue: "#0084FF",
  /** The panel's ground and the agent's bubble, on a light and a dark host. */
  surface: { light: "#ffffff", dark: "#242526" },
  bubble: { light: "#f5f5f5", dark: "#404040" },
  /** Secondary text - the header buttons, the initials, the composer hint. */
  muted: { light: "#8e8e8e", dark: "#a8a8a8" },
  border: { light: "rgba(0,0,0,.1)", dark: "rgba(255,255,255,.12)" },

  panelWidth: 372,
  panelHeight: 520,
  panelRadius: 24,
  /** Between the launcher's top and the panel's bottom. */
  panelGap: 14,
  panelShadow: "0 8px 32px rgba(0,0,0,.16)",

  launcherSize: 60,
  launcherShadow: "0 6px 20px rgba(123,91,255,.4)",
  /** The Messenger mark on the launcher's corner - a 24-box fill path. */
  badgeSize: 20,
  logo: "M12 2C6.48 2 2 6.18 2 11.32c0 2.93 1.45 5.55 3.7 7.27v3.4l3.39-1.86a10.5 10.5 0 0 0 2.91.41c5.52 0 10-4.18 10-9.22S17.52 2 12 2Zm1.06 12.42-2.55-2.72-4.97 2.72 5.46-5.8 2.61 2.72 4.91-2.72-5.46 5.8Z",

  headerAvatar: 40,
  headerButton: 28,
  headerIcon: 18,
  nameFont: 15,

  /** Beside the first bubble of an agent's run. */
  avatarSize: 28,
  bubbleRadius: 18,
  bubbleFont: 14,
  bubblePadding: "6px 12px",
  bubbleMaxWidth: "78%",

  ctaShadow: "0 4px 12px rgba(123,91,255,.32)",

  composerPlaceholder: "Aa",
  composerRadius: 24,
  composerHeight: 36,
  sendSize: 36,
  sendIcon: 20,
} as const
